/**
 * ErrorBanner — Inline error alert with optional title, retry/action button,
 * and dismiss button. Replaces ad-hoc `bg-red-50 dark:bg-red-900/20` error
 * patterns used across all shells AND the bespoke per-app ErrorBanner /
 * QueryErrorBanner copies in email/, crm/, reviews/, social/.
 *
 * The retry button is sized to meet WCAG 2.5.5 (target size — minimum 44x44 CSS
 * pixels) so error states are reachable from touch on mobile / tablet. Origin:
 * AI app health audit 2026-05-20 found 15+ inline Retry buttons sized
 * `px-3 py-1 text-xs` (~28px tall).
 *
 * Two action affordances are supported:
 *
 *  - `onRetry` — Renders a block-style retry button (`bg-danger-border`),
 *    a leading `RefreshCw` icon, and a default "Retry" label. Use for the
 *    common "fetch failed, click to refetch" pattern.
 *
 *  - `onAction` — Renders an inline text-link affordance (underline at rest,
 *    `hover:no-underline` flip, red focus-visible ring). Use for arbitrary
 *    actions ("Dismiss", "Try again", "Configure", "Open settings"). The
 *    underline-at-rest is required for WCAG 1.4.1 (Use of Color) — links must
 *    have a non-color visible affordance at rest, not just on hover. Origin:
 *    apps_visual_dark_a11y_sweep_wave2_handoff_2026-05-24 — email app fix that
 *    is now consolidated here for the React SPA fleet.
 *
 * The `label` + `error` props auto-derive a message when no explicit `message`
 * is supplied. Use for TanStack Query failure surfaces — they replace the
 * bespoke `QueryErrorBanner` previously in social/.
 *
 *  - With `message`         → message is rendered verbatim.
 *  - With `error` + `label` → "Failed to load {label}: {error.message}".
 *  - With `label` only      → "Failed to load {label}. Please try again.".
 *  - With neither           → "Failed to load data. Please try again.".
 *
 * @layer widget
 * @package @plerity/dashboard-shared
 */

import { clsx } from "clsx";
import { AlertCircle, RefreshCw, X } from "lucide-react";

// ============================================================================
// Types
// ============================================================================

export interface ErrorBannerProps {
  /**
   * Primary error message. When omitted, a message is derived from `error` /
   * `label` (see header docs). Most callers should supply this directly.
   */
  message?: string;
  /**
   * Bold title displayed above the message. RECOMMENDED: pass a short,
   * page-specific title ("Couldn't load keywords") so screen-reader users hear
   * WHAT failed before the detail — `role="alert"` announces the whole banner.
   * Optional only for backward compatibility; surface reviews flag its absence.
   */
  title?: string;
  /**
   * If provided, renders a block-style Retry button (with `RefreshCw` icon)
   * that calls this on click. Use this for the canonical "fetch failed, retry"
   * pattern. The retry button meets WCAG 2.5.5 44x44 touch target.
   */
  onRetry?: () => void;
  /** Label for the retry button (default: "Retry") */
  retryLabel?: string;
  /**
   * If provided, renders an inline text-link action affordance instead of a
   * block button. The link has `underline underline-offset-2` at rest,
   * `hover:no-underline`, and a red focus-visible ring. Use for actions like
   * "Dismiss", "Configure", "Open settings" where a block button would be
   * visually heavier than warranted. Requires `actionLabel`.
   *
   * If both `onRetry` and `onAction` are provided, the retry block button is
   * rendered (the inline action is suppressed) — pick one affordance per
   * banner.
   */
  onAction?: () => void;
  /** Label for the inline text-link action (required when `onAction` is set) */
  actionLabel?: string;
  /** If provided, renders a dismiss (×) button that calls this on click */
  onDismiss?: () => void;
  /** Additional Tailwind classes applied to the root element */
  className?: string;
  /**
   * Context label used to derive the message when `message` is not supplied.
   * Example: `label="campaigns"` → "Failed to load campaigns. Please try again."
   * Use together with `error` for richer messages.
   */
  label?: string;
  /**
   * Error object (typically from TanStack Query) used to derive the message
   * when `message` is not supplied. The `error.message` is appended after
   * "Failed to load {label}: ".
   */
  error?: Error | null;
}

// ============================================================================
// Internal helpers
// ============================================================================

function deriveMessage(props: ErrorBannerProps): string {
  if (props.message != null) return props.message;
  const label = props.label ?? "data";
  if (props.error?.message) return `Failed to load ${label}: ${props.error.message}`;
  return `Failed to load ${label}. Please try again.`;
}

// ============================================================================
// Component
// ============================================================================

export function ErrorBanner(props: ErrorBannerProps) {
  const { title, onRetry, retryLabel = "Retry", onAction, actionLabel, onDismiss, className } = props;
  const message = deriveMessage(props);

  // `onRetry` wins over `onAction` if both supplied — see header docs.
  const showRetry = onRetry != null;
  const showAction = !showRetry && onAction != null && actionLabel != null;

  return (
    <div
      role="alert"
      className={clsx(
        "flex items-start gap-3 rounded-lg border p-4",
        // Semantic danger tokens flip per mode via the html.dark runtime block,
        // so ONE class covers light + dark (no `dark:` double-flip). Contrast:
        // danger-strong on danger-bg = #991b1b/#fef2f2 ~7.7:1 light,
        // #fca5a5/#450a0a ~9:1 dark. Origin: shared-package-2026-10-22-001 (1).
        "bg-danger-bg border-danger-border text-danger-strong",
        className
      )}
    >
      {/* Icon */}
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger-strong" aria-hidden="true" />

      {/* Text content + action */}
      <div className="flex-1 min-w-0">
        {title && <p className="mb-1 font-semibold text-danger-strong">{title}</p>}
        <p className="text-sm">{message}</p>
        {showRetry && (
          <button
            type="button"
            onClick={onRetry}
            className={clsx(
              // Sizing — meets WCAG 2.5.5 44x44px minimum touch target via min-h/min-w-11.
              "mt-3 inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-md px-4 py-2",
              "text-sm font-medium",
              // Solid danger-border fill: danger-strong on it = #991b1b/#fecaca
              // ~5.6:1 light, #fca5a5/#7f1d1d ~5.3:1 dark (AA). Hover blends the
              // fill toward the banner's danger-bg, which only RAISES contrast.
              // (Prior raw pair: errorbanner-2026-06-18-001 — solid red-900, not
              // /40, so the dark text cleared AA; the semantic tokens keep that.)
              "bg-danger-border text-danger-strong hover:bg-danger-border/60",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger"
            )}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {retryLabel}
          </button>
        )}
        {showAction && (
          <button
            type="button"
            onClick={onAction}
            // a11y 1.4.1: visible non-color affordance (underline) at rest, not only
            // on hover. Origin: visual-email-errorbanner-affordance-2026-05-24-001.
            // Consolidated for the React SPA fleet via
            // visual-errorbanner-shared-consolidation-2026-05-25-001.
            className={clsx(
              "mt-2 inline-flex items-center text-sm font-medium rounded",
              "underline underline-offset-2 hover:no-underline",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger"
            )}
          >
            {actionLabel}
          </button>
        )}
      </div>

      {/* Dismiss button — sized to meet WCAG 2.5.5 44x44 minimum touch target */}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss error"
          className={clsx(
            // min-h-11/min-w-11 + flex centering pads the visual icon out to a 44px hit area
            // without growing the visible chrome.
            "inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded transition-colors",
            "text-danger-strong hover:bg-danger-border",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger"
          )}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
